import React, { createContext, useContext, useState, useEffect } from 'react';
import authService, { User, LoginResponse } from '../services/auth';
import apiService from '../services/api';

const SELECTED_AGENCY_KEY = 'dbf_selected_agency';

export interface AgencyOption {
  agency_id: string;
  name: string;
  role: string;
  logo_small_url: string | null;
  primary_color: string | null;
}

interface AuthContextType {
  user: User | null;
  agencyRole: string | null;
  serverRegion: string | null;
  branding: {
    logo_url: string | null;
    logo_small_url: string | null;
    login_logo_url: string | null;
    favicon_url: string | null;
    primary_color: string | null;
    accent_color: string | null;
    show_powered_by: boolean;
    company_name: string | null;
  } | null;
  agencyId: string | null;
  agencyName: string | null;
  agencies: AgencyOption[];
  loading: boolean;
  login: (email: string, password: string, twoFactorCode?: string, twoFactorMethod?: string, rememberDevice?: boolean) => Promise<LoginResponse>;
  register: (data: any) => Promise<any>;
  requestMagicLink: (email: string) => Promise<void>;
  logout: () => void;
  setUser: (user: User | null) => void;
  refreshAgencyRole: () => Promise<void>;
  switchAgency: (agencyId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [agencyRole, setAgencyRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [serverRegion, setServerRegion] = useState<string | null>(null);
  const [branding, setBranding] = useState<AuthContextType['branding']>(null);
  const [agencyId, setAgencyId] = useState<string | null>(null);
  const [agencyName, setAgencyName] = useState<string | null>(null);
  const [agencies, setAgencies] = useState<AgencyOption[]>([]);

  const applyBranding = (brandingData: any) => {
    if (brandingData?.primary_color) {
      document.documentElement.style.setProperty('--v2-primary', brandingData.primary_color);
    }
    if (brandingData?.accent_color) {
      document.documentElement.style.setProperty('--v2-accent', brandingData.accent_color);
    }
    if (brandingData?.favicon_url) {
      const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement
        || document.createElement('link');
      link.rel = 'icon';
      link.href = brandingData.favicon_url;
      document.head.appendChild(link);
    }
  };

  const loadAgencies = async (): Promise<AgencyOption[]> => {
    try {
      const response = await apiService.get('/agencies/list');
      const list: AgencyOption[] = response.data.agencies || [];
      setAgencies(list);
      return list;
    } catch (error) {
      console.error('Failed to load agency list:', error);
      return [];
    }
  };

  const loadAgencyRole = async (targetAgencyId?: string) => {
    try {
      const url = targetAgencyId
        ? `/agencies/current?agencyId=${targetAgencyId}`
        : '/agencies/current';
      const response = await apiService.get(url);
      setAgencyRole(response.data.my_role || null);
      setServerRegion(response.data.server_id || 'US');
      setBranding(response.data.branding || null);
      setAgencyId(response.data.agency_id || null);
      setAgencyName(response.data.name || null);
      applyBranding(response.data.branding);
    } catch (error) {
      console.error('Failed to load agency role:', error);
      setAgencyRole(null);
      setServerRegion(null);
      setBranding(null);
      setAgencyId(null);
      setAgencyName(null);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = authService.getAccessToken();
      if (token) {
        try {
          const response = await apiService.get('/profile');
          setUser(response.data.user);
          const list = await loadAgencies();
          const savedId = localStorage.getItem(SELECTED_AGENCY_KEY);
          const validSaved = savedId && list.some(a => a.agency_id === savedId) ? savedId : undefined;
          await loadAgencyRole(validSaved || undefined);
          if (validSaved) setAgencyId(validSaved);
        } catch (error) {
          authService.logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (
    email: string,
    password: string,
    twoFactorCode?: string,
    twoFactorMethod?: string,
    rememberDevice?: boolean
  ): Promise<LoginResponse> => {
    const response = await authService.login(email, password, twoFactorCode, twoFactorMethod, rememberDevice);

    if ('requiresTwoFactor' in response && response.requiresTwoFactor) {
      return response;
    }

    if ('user' in response) {
      setUser(response.user);
      const list = await loadAgencies();
      const savedId = localStorage.getItem(SELECTED_AGENCY_KEY);
      const validSaved = savedId && list.some(a => a.agency_id === savedId) ? savedId : undefined;
      await loadAgencyRole(validSaved || undefined);

      // If multiple agencies and no saved preference, signal caller to show selector
      if (list.length > 1 && !validSaved) {
        (response as any)._needsAgencySelection = true;
        (response as any)._agencies = list;
      }
    }

    return response;
  };

  const switchAgency = async (newAgencyId: string) => {
    localStorage.setItem(SELECTED_AGENCY_KEY, newAgencyId);
    // Reset CSS variables before loading new branding
    document.documentElement.style.removeProperty('--v2-primary');
    document.documentElement.style.removeProperty('--v2-accent');
    await loadAgencyRole(newAgencyId);
  };

  const register = async (data: any) => {
    const response = await authService.register(data);
    if (response.success) {
      await login(data.email, data.password);
    }
    return response;
  };

  const requestMagicLink = async (email: string) => {
    await authService.requestMagicLink(email);
  };

  const logout = () => {
    authService.logout();
    localStorage.removeItem(SELECTED_AGENCY_KEY);
    setUser(null);
    setAgencyRole(null);
    setServerRegion(null);
    setBranding(null);
    setAgencyId(null);
    setAgencyName(null);
    setAgencies([]);
    document.documentElement.style.removeProperty('--v2-primary');
    document.documentElement.style.removeProperty('--v2-accent');
  };

  const refreshAgencyRole = async () => {
    const savedId = localStorage.getItem(SELECTED_AGENCY_KEY);
    await loadAgencyRole(savedId || undefined);
  };

  const value: AuthContextType = {
    user,
    agencyRole,
    serverRegion,
    loading,
    login,
    register,
    requestMagicLink,
    logout,
    setUser,
    refreshAgencyRole,
    branding,
    agencyId,
    agencyName,
    agencies,
    switchAgency,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
